/**
 * Custom elements — the zero-JS, framework-agnostic tier. Drop
 * `<stoq-preorder-widget variant-id="…">` into any storefront's HTML and it
 * renders the preorder CTA (badge + button, optional notify-me fallback),
 * re-rendering itself whenever its attributes change.
 *
 * Design: **attributes in, events out.**
 * - Inputs are attributes (`variant-id`, `product-id`, …). Changing one
 *   re-renders in place — so a framework binding (`variant-id={…}`) or a manual
 *   `setAttribute` is all you need to rebind on variant change. No imperative
 *   `update()` call.
 * - Add-to-cart can't be a callback (HTML attributes hold no functions), so it
 *   surfaces as a `stoq:add-to-cart` CustomEvent (`detail: { line,
 *   confirmation }`, bubbles + composed) — listen for it and add the line to
 *   the cart.
 * - Optional `watch="<selector>"` binds to a vanilla `<select name="id">` /
 *   input: its `change` copies the value to `variant-id` (for no-framework
 *   storefronts).
 *
 * SSR-safe: the element classes are defined lazily inside
 * {@link defineStoqElements} (only ever called in a browser), so importing this
 * module never touches `HTMLElement` / `customElements` at load.
 */
import type { StoqClient } from './client'
import { warn } from './util'
import type { VariantInput, VariantObjectInput } from './variant-input'
import {
  renderNotifyMe,
  renderPreorderBadge,
  renderPreorderWidget,
  type WidgetHandle,
} from './widgets'

/** Resolves the client used by every `<stoq-*>` element (shared, set on define). */
type ClientProvider = () => StoqClient | Promise<StoqClient>

let clientProvider: ClientProvider | null = null
let defined = false

/**
 * Registers the `<stoq-preorder-widget>`, `<stoq-preorder-badge>` and
 * `<stoq-notify-me>` custom elements, wiring them to `client` (a
 * {@link StoqClient}, or a function returning one / a promise of one). Safe to
 * call more than once — re-points the client; defines each tag only once.
 * No-op outside a browser (no `customElements`).
 *
 * The IIFE/CDN build calls this automatically on auto-init, so script-tag
 * storefronts get the elements for free. npm/ESM consumers call it with their
 * own `init()`-ed client.
 */
export function defineStoqElements(client: StoqClient | ClientProvider): void {
  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return
  clientProvider = typeof client === 'function' ? (client as ClientProvider) : () => client
  if (defined) return
  defined = true

  function getClient(): Promise<StoqClient> | null {
    if (!clientProvider) return null
    return Promise.resolve(clientProvider())
  }

  /** Shared lifecycle: attribute-reactive render into the element's own subtree. */
  abstract class StoqElement extends HTMLElement {
    protected handle: WidgetHandle | null = null
    private pending = false
    private watchEl: Element | null = null
    private readonly onWatchChange = (): void => {
      const value = (this.watchEl as HTMLInputElement | HTMLSelectElement | null)?.value
      if (value) this.setAttribute('variant-id', value)
    }

    /** Renders the widget for the current attributes, or null to show nothing. */
    protected abstract render(client: StoqClient): Promise<WidgetHandle | null>

    connectedCallback(): void {
      this.setupWatch()
      this.scheduleRender()
    }

    disconnectedCallback(): void {
      if (this.watchEl) this.watchEl.removeEventListener('change', this.onWatchChange)
      this.watchEl = null
      this.handle?.destroy()
      this.handle = null
    }

    attributeChangedCallback(): void {
      if (this.isConnected) this.scheduleRender()
    }

    /** Coalesce multiple attribute writes in one tick into a single render. */
    private scheduleRender(): void {
      if (this.pending) return
      this.pending = true
      queueMicrotask(() => {
        this.pending = false
        if (!this.isConnected) return
        const client = getClient()
        if (!client) {
          warn('<stoq-*> used before defineStoqElements() set a client')
          return
        }
        void client
          .then((c) => this.render(c))
          .then((handle) => {
            this.handle = handle
          })
          .catch((error: unknown) => warn('stoq element render failed', error))
      })
    }

    private setupWatch(): void {
      const selector = this.getAttribute('watch')
      if (!selector) return
      const root = this.getRootNode() as Document | ShadowRoot
      const target = root.querySelector?.(selector) ?? document.querySelector(selector)
      if (!target) {
        warn(`<stoq-*> watch target not found: ${selector}`)
        return
      }
      this.watchEl = target
      target.addEventListener('change', this.onWatchChange)
    }

    // --- attribute helpers --------------------------------------------------
    protected str(name: string): string | undefined {
      return this.getAttribute(name) ?? undefined
    }

    protected bool(name: string): boolean {
      const value = this.getAttribute(name)
      return value !== null && value !== 'false'
    }

    protected num(name: string): number | undefined {
      const value = this.getAttribute(name)
      if (value === null) return undefined
      const parsed = Number(value)
      return Number.isFinite(parsed) ? parsed : undefined
    }

    /** A bare id, or an object carrying any availability attributes that are set. */
    protected variant(): VariantInput {
      const id = this.getAttribute('variant-id') ?? ''
      const obj: VariantObjectInput = { id }
      let hasAvailability = false
      if (this.hasAttribute('available-for-sale')) {
        obj.availableForSale = this.bool('available-for-sale')
        hasAvailability = true
      }
      if (this.hasAttribute('currently-not-in-stock')) {
        obj.currentlyNotInStock = this.bool('currently-not-in-stock')
        hasAvailability = true
      }
      return hasAvailability ? obj : id
    }

    protected emitAddToCart = (line: unknown, confirmation: unknown): void => {
      this.dispatchEvent(
        new CustomEvent('stoq:add-to-cart', {
          detail: { line, confirmation },
          bubbles: true,
          composed: true,
        }),
      )
    }

    protected requireVariantId(tag: string): boolean {
      if (this.getAttribute('variant-id')) return true
      warn(`<${tag}> requires a variant-id attribute`)
      this.handle?.destroy()
      this.handle = null
      return false
    }
  }

  class StoqPreorderWidgetElement extends StoqElement {
    static get observedAttributes(): string[] {
      return [
        'variant-id', 'product-id', 'product-title', 'variant-title', 'image-url',
        'available-for-sale', 'currently-not-in-stock', 'quantity',
        'badge', 'badge-text', 'notify-me-fallback', 'label',
      ]
    }

    protected render(client: StoqClient): Promise<WidgetHandle | null> {
      if (!this.requireVariantId('stoq-preorder-widget')) return Promise.resolve(null)
      const badge = this.bool('badge')
        ? (this.str('badge-text') !== undefined ? { text: this.str('badge-text') } : true)
        : undefined
      return renderPreorderWidget(client, this.variant(), {
        container: this,
        onAddToCart: this.emitAddToCart,
        quantity: this.num('quantity'),
        productId: this.str('product-id'),
        productTitle: this.str('product-title'),
        variantTitle: this.str('variant-title'),
        imageUrl: this.str('image-url'),
        label: this.str('label'),
        badge,
        notifyMeFallback: this.bool('notify-me-fallback'),
      })
    }
  }

  class StoqPreorderBadgeElement extends StoqElement {
    static get observedAttributes(): string[] {
      return ['variant-id', 'available-for-sale', 'currently-not-in-stock', 'text']
    }

    protected render(client: StoqClient): Promise<WidgetHandle | null> {
      if (!this.requireVariantId('stoq-preorder-badge')) return Promise.resolve(null)
      return renderPreorderBadge(client, this.variant(), {
        container: this,
        text: this.str('text'),
      })
    }
  }

  class StoqNotifyMeElement extends StoqElement {
    static get observedAttributes(): string[] {
      return [
        'variant-id', 'product-id', 'product-title', 'variant-title', 'image-url',
        'expanded', 'label',
      ]
    }

    protected render(client: StoqClient): Promise<WidgetHandle | null> {
      if (!this.requireVariantId('stoq-notify-me')) return Promise.resolve(null)
      const productId = this.str('product-id')
      if (productId === undefined) {
        warn('<stoq-notify-me> requires a product-id attribute')
        this.handle?.destroy()
        this.handle = null
        return Promise.resolve(null)
      }
      return renderNotifyMe(client, this.variant(), {
        container: this,
        productId,
        productTitle: this.str('product-title'),
        variantTitle: this.str('variant-title'),
        imageUrl: this.str('image-url'),
        expanded: this.bool('expanded'),
        label: this.str('label'),
      })
    }
  }

  const tags: Array<[string, CustomElementConstructor]> = [
    ['stoq-preorder-widget', StoqPreorderWidgetElement],
    ['stoq-preorder-badge', StoqPreorderBadgeElement],
    ['stoq-notify-me', StoqNotifyMeElement],
  ]
  for (const [tag, ctor] of tags) {
    if (!customElements.get(tag)) customElements.define(tag, ctor)
  }
}
